// Combat engine with all card effect support.
import { CARDS, STARTER_DECK, isEthereal, isInnate, isRetain, isCursed } from './cards';
import { makeRng, shuffle } from './rng';
import { CARD_POOL_COMMON, CARD_POOL_UNCOMMON, CARD_POOL_RARE } from './cards';
import { TAG_SYNERGIES, activeRelicSets } from './depth';
import { starterDeckFor } from './classes';

export const MAX_HAND = 10;

export function newPlayer(seed = 'START', classDef = null, mods = {}) {
  let hp = classDef?.startingHp ?? 60;
  if (mods.hpDelta) hp = Math.max(15, hp + mods.hpDelta);
  if (mods.hpMult) hp = Math.max(15, Math.floor(hp * mods.hpMult));
  const startGold = mods.startGold ?? 0;
  let maxEnergy = 3 + (mods.energyDelta || 0);
  if (maxEnergy < 2) maxEnergy = 2;
  const deck = classDef ? starterDeckFor(classDef, mods.mastery || 0, !!mods.useAltStarter) : [...STARTER_DECK];
  return {
    hp,
    maxHp: hp,
    gold: startGold,
    energy: maxEnergy,
    maxEnergy,
    strength: 0,
    seed,
    deck,
    relics: classDef?.startingRelic ? [classDef.startingRelic] : [],
    classId: classDef?.id ?? null,
    mods: mods || {},
  };
}

// Combat state
export function startCombat(player, enemyDef, rngSeed) {
  const rng = makeRng(rngSeed);
  const mods = player.mods || {};
  let bonusEnergy = 0;
  let startBlock = 0;
  let extraDraw = 0;
  if (player.relics.includes('burnerphone')) bonusEnergy += 1;
  if (player.relics.includes('energydrink')) startBlock += 5;
  if (player.relics.includes('ringlight')) extraDraw += 1;
  if (player.relics.includes('loreleash')) extraDraw += 1;
  // v0.8: apply relic sets
  const relicSetMods = { startBlockBonus: 0, strPerCombat: 0, energyDelta: 0 };
  const setsActive = activeRelicSets(player.relics);
  for (const s of setsActive) s.apply(relicSetMods);
  startBlock += relicSetMods.startBlockBonus || 0;
  if (relicSetMods.energyDelta) bonusEnergy += relicSetMods.energyDelta;
  // proteinshake individual
  if (player.relics.includes('proteinshake')) player.strength = (player.strength || 0) + 1;
  // v1.8: STREAK RING — +1 STR per current run streak (max +5)
  if (player.relics.includes('streakring')) {
    const streak = Math.min(5, player._runStreak || 0);
    player.strength = (player.strength || 0) + streak;
  }
  // v1.8: AURA BANK — mark for later energy bump every 3rd turn (in newTurn)
  // v1.8: ECHO SHARD — first attack this combat is played twice (flag reset per combat)
  // v1.8: PARADOX POUCH / ECHO SHARD state flags
  // Track in state.player._echoShardUsed and state.player._paradoxUsed via later mechanics.
  // v0.9: boss/elite relics
  if (player.relics.includes('cursedcrown')) player.strength = (player.strength || 0) + 2;
  if (player.relics.includes('eldergpu')) bonusEnergy += 2;  // turn 1 energy boost
  // v0.9: signature-boon per-combat effects
  if (player._sigDraw) extraDraw += player._sigDraw;
  if (player._sigEnergy) bonusEnergy += player._sigEnergy;
  if (player._sigStartBlock) startBlock += player._sigStartBlock;
  // v0.9: first-kill boon
  const firstKillDmg = player._firstKill || 0;

  // Apply enemy HP modifiers
  let scaledMaxHp = enemyDef.maxHp;
  if (enemyDef.isBoss && mods.bossHpMult) scaledMaxHp = Math.floor(scaledMaxHp * mods.bossHpMult);
  if (enemyDef.isElite && mods.eliteHpMult) scaledMaxHp = Math.floor(scaledMaxHp * mods.eliteHpMult);
  if (mods.enemyHpMult) scaledMaxHp = Math.floor(scaledMaxHp * mods.enemyHpMult);

  const enemy = {
    id: enemyDef.id,
    name: enemyDef.name,
    sprite: enemyDef.sprite,
    hp: scaledMaxHp,
    maxHp: scaledMaxHp,
    block: enemyDef.startBlock || 0,
    strength: (mods.enemyStrength || 0) + (enemyDef.strength || 0),
    weak: 0,
    vulnerable: 0,
    intents: enemyDef.intents,
    intentIdx: Math.floor(rng() * enemyDef.intents.length),
    isBoss: !!enemyDef.isBoss,
    isElite: !!enemyDef.isElite,
    isFinal: !!enemyDef.isFinal,
    phases: enemyDef.phases,
    // v0.8 affixes
    affix: enemyDef.affix,
    vampiric: enemyDef.vampiric,
    deathBomb: enemyDef.deathBomb,
    applyExtraWeak: enemyDef.applyExtraWeak,
    dmgMult: enemyDef.dmgMult || 1,
    // v3.3 elite modifier hooks
    regenPerTurn: enemyDef.regenPerTurn || 0,
    barbedThorns: enemyDef.barbedThorns || 0,
    doubledIntent: enemyDef.doubledIntent || 1,
    perTurnShield: enemyDef.perTurnShield || 0,
    _shieldConsumedThisTurn: 0,
    // v0.9 combat modifier
    blockPerTurn: mods.blockPerTurn || enemyDef.blockPerTurn || 0,
    // v3.4 elite affixes (defense-in-depth propagation)
    applyPoisonOnHit: enemyDef.applyPoisonOnHit || 0,
    livestreamed: !!enemyDef.livestreamed,
  };

  if (player.relics.includes('algobreaker')) enemy.weak = 1;
  // v0.9: first-kill boon zaps first enemy
  if (firstKillDmg > 0 && !enemyDef.isBoss) {
    enemy.hp = 0;
    player._firstKill = 0;
  }
  // v3.5 EVENT: HIRE A MERC — one-shot auto-clear on next elite
  if (enemy.isElite && player._mercAvail) {
    enemy.hp = 0;
    player._mercAvail = false;
    player._mercUsed = true;
  }

  const drawPile = shuffle(rng, [...player.deck]);
  // v0.9: permanent buffs earned during the run apply to this combat too
  const permaStr = player._permaStrength || 0;
  const permaHpDelta = player._permaMaxHp || 0;
  const permaDmgMult = player._permaDamageMult || 1;
  if (permaHpDelta && player.maxHp + permaHpDelta > 0) {
    // apply once when combat starts (idempotent flag)
    if (!player._permaHpApplied) {
      player.maxHp = Math.max(15, player.maxHp + permaHpDelta);
      player.hp = Math.min(player.hp, player.maxHp);
      player._permaHpApplied = true;
    }
  }
  // v2.1 BOSS BLESSINGS — apply modifiers stored on player._blessMods
  const bMods = player._blessMods || {};
  const blessStartStr = bMods.blessStartStr || 0;
  if (bMods.blessEnergyDelta) bonusEnergy += bMods.blessEnergyDelta;
  if (bMods.blessStartBlock) startBlock += bMods.blessStartBlock;
  if (bMods.blessDrawDelta) extraDraw += bMods.blessDrawDelta;
  // v3.3 PERKS: extra draw perk applied BEFORE state construction so it flows into state.extraDraw
  if (player._perkExtraDrawEach) extraDraw += player._perkExtraDrawEach;
  // v2.3 LEGENDARY: ASCENSION CROWN — stacking +5 HP + +1 STR per crown, applied idempotently as combats pass.
  const crowns = bMods.blessAscensionCrown || 0;
  const ascHpApplied = player._ascCrownHpApplied || 0;
  if (crowns > ascHpApplied) {
    const delta = (crowns - ascHpApplied) * 5;
    player.maxHp += delta;
    player.hp = Math.min(player.maxHp, player.hp + delta);
    player._ascCrownHpApplied = crowns;
  }
  const ascStrBonus = crowns; // +1 str per crown applied to every combat
  const state = {
    rngSeed,
    player: {
      ...player,
      block: 0 + startBlock,
      energy: player.maxEnergy + bonusEnergy,
      strength: (player.strength || 0) + (relicSetMods.strPerCombat || 0) + permaStr + blessStartStr + ascStrBonus,
      weak: 0,
      vulnerable: 0,
      reflect: 0,
      reflectAmount: 0,
      dodgeNext: false,
    },
    enemy,
    drawPile,
    hand: [],
    discardPile: [],
    exhaustPile: [],
    turn: 1,
    log: [],
    turnOwner: 'player',
    extraDraw,
    powerDrawExtra: 0,
    powerStrengthGain: 0,
    powerBlockPerTurn: 0,
    powerDoubleWeak: false,
    powerReduceIncoming: 0,
    attacksThisTurn: 0,
    intentDamageMod: 0,
    playerDamageMult: mods.playerDamageMult || 1,
    powersPlayedThisCombat: 0,
    cardsPlayedThisCombat: 0,     // v2.3 QoL for RunStatsHUD
    dmgDealtThisCombat: 0,        // v2.3 QoL for RunStatsHUD
    dmgTakenThisCombat: 0,
    biggestSingleHit: 0,
    // combo tracking
    tagCountsTurn: {},     // tag -> count this turn
    tagCountsCombat: {},   // tag -> count this combat
    uniqueTagsCombat: 0,
    lastTurnHadRatio: false,
    thisTurnHasRatio: false,
    // retain bonuses per hand slot (card instance id -> accumulated bonus)
    retainDmgStack: 0,
    retainBlockStack: 0,
    // multi-phase boss
    phase: 0,
    ended: false,
    victory: false,
  };

  // v3.3 PERKS: apply persistent hooks at combat start
  if (player._perkStartStr) state.player.strength += player._perkStartStr;
  if (player._perkStartBlock) state.player.block = (state.player.block || 0) + player._perkStartBlock;
  if (player._perkCombatStartHeal) state.player.hp = Math.min(state.player.maxHp, state.player.hp + player._perkCombatStartHeal);
  if (player._perkFirstAttackFree) state._perkFirstAttackFreeAvail = true;

  // Pull INNATE cards into hand first, then draw the remaining opening hand.
  const innateInDeck = drawPile.filter(c => isInnate(c));
  const innatePicked = [];
  for (const cid of innateInDeck) {
    const idx = state.drawPile.indexOf(cid);
    if (idx >= 0 && state.hand.length < MAX_HAND) {
      state.drawPile.splice(idx, 1);
      state.hand.push(cid);
      innatePicked.push(cid);
    }
  }
  drawCards(state, Math.max(0, 5 + extraDraw - innatePicked.length));
  return state;
}

function log(state, msg) {
  state.log.push(msg);
  if (state.log.length > 40) state.log.shift();
}

export function drawCards(state, n) {
  for (let i = 0; i < n; i++) {
    if (state.hand.length >= MAX_HAND) break;
    if (state.drawPile.length === 0) {
      if (state.discardPile.length === 0) break;
      const rng = makeRng(state.rngSeed + '-shuf-' + state.turn + '-' + i);
      state.drawPile = shuffle(rng, state.discardPile);
      state.discardPile = [];
    }
    const c = state.drawPile.shift();
    // v1.9: SLOP FILTER relic — first 2 STATUS/CURSE cards drawn each combat auto-exhaust
    if (state.player.relics?.includes('slopfilter')) {
      const cardObj = CARDS[c];
      if (cardObj && cardObj.curse && (state._slopFilterUsed || 0) < 2) {
        state.exhaustPile.push(c);
        state._slopFilterUsed = (state._slopFilterUsed || 0) + 1;
        log(state, `SLOP FILTER: exhausted ${cardObj.name}`);
        i--; // Try to draw again
        continue;
      }
    }
    state.hand.push(c);
  }
}

export function playCard(state, handIdx) {
  if (state.ended || state.turnOwner !== 'player') return state;
  const cardId = state.hand[handIdx];
  const card = CARDS[cardId];
  if (!card) return state;
  // v1.5: STATUS / CURSE cards can't be played
  if (card.unplayable) {
    log(state, `${card.name} is UNPLAYABLE.`);
    return state;
  }
  // X-cost cards: cost is 'X' (or numeric). If X, spend all remaining energy.
  const isXcost = card.cost === 'X' || card.xCostAll;
  let numericCost = isXcost ? 0 : card.cost;
  // v0.9: FREE TIER cost reduction
  if (state._costReduction && !isXcost) numericCost = Math.max(0, numericCost - state._costReduction);
  // v2.3 LEGENDARY: BASIC INSTINCT — all 1-cost cards cost 0.
  if (state.player._blessMods && state.player._blessMods.blessOneCostFree && !isXcost && card.cost === 1) {
    numericCost = 0;
  }
  // v2.8 DIVINE: SIGMA PROTOCOL — all cards cost 0 this turn.
  if (state._zeroCostThisTurn && !isXcost) numericCost = 0;
  // v3.3 PERK: PREEMPTIVE STRIKE — first attack of combat costs 0
  if (state._perkFirstAttackFreeAvail && card.type === 'attack' && !isXcost) {
    numericCost = 0;
    state._perkFirstAttackFreeAvail = false;
  }
  // v2.1 BOSS BLESSING: FREE FIRST — first card each turn costs 0
  const _blessFirstFree = !!(state.player._blessMods && state.player._blessMods.blessFirstCardFree);
  if (_blessFirstFree && !state._blessFirstFiredThisTurn && !isXcost) {
    numericCost = 0;
    state._blessFirstFiredThisTurn = true;
  }
  if (!isXcost && state.player.energy < numericCost) return state;
  if (card.requiresBlock && state.player.block <= 0) return state;

  const xSpent = isXcost ? state.player.energy : 0;
  state.player.energy -= (isXcost ? xSpent : numericCost);
  state.cardsPlayedThisCombat = (state.cardsPlayedThisCombat || 0) + 1;
  // v2.1 CARD AUDIT FIX: snapshot enemy debuffs BEFORE this card applies its own — so
  // "if enemy was ALREADY debuffed" checks (e.g. PREMIUM RATIO gainEnergyOnDebuffed) work as advertised.
  const _wasDebuffedPrePlay = (state.enemy.weak > 0 || state.enemy.vulnerable > 0);
  // Combo: apply cost reduction for cheaperIfRatioLastTurn (already paid full above; refund)
  if (card.cheaperIfRatioLastTurn && state.lastTurnHadRatio) {
    state.player.energy += Math.min(card.cheaperIfRatioLastTurn, numericCost);
  }
  // Track tags played this turn/combat (before effects that depend on it)
  const tags = card.tags || [];
  for (const t of tags) {
    if (!state.tagCountsTurn[t]) state.tagCountsTurn[t] = 0;
    state.tagCountsTurn[t] += 1;
    if (!state.tagCountsCombat[t]) {
      state.tagCountsCombat[t] = 0;
      state.uniqueTagsCombat += 1;
    }
    state.tagCountsCombat[t] += 1;
    if (t === 'ratio') state.thisTurnHasRatio = true;
  }

  // v0.8: Tag synergies fire once per turn when threshold reached
  // v2.1 META SYNERGY blessing: reduce threshold by N (min 2)
  const _synReduce = state.player._blessMods?.blessSynergyReduce || 0;
  state.synergiesFiredTurn = state.synergiesFiredTurn || {};
  for (const t of tags) {
    const syn = TAG_SYNERGIES[t];
    const thr = syn ? Math.max(2, syn.threshold - _synReduce) : 0;
    if (syn && !state.synergiesFiredTurn[t] && (state.tagCountsTurn[t] || 0) >= thr) {
      state.synergiesFiredTurn[t] = true;
      syn.apply(state);
      if (state._synergyDraw) { drawCards(state, state._synergyDraw); state._synergyDraw = 0; }
      log(state, `SYNERGY: ${syn.name}!`);
      state._lastSynergy = syn.name;
      state._synergyCount = (state._synergyCount || 0) + 1;
    }
  }

  if (card.type === 'attack') {
    state.attacksThisTurn += 1;
    // v0.9.5: x_cost_god achievement — playing X-cost with 5+ energy
    if (isXcost && xSpent >= 5) {
      state._achievement_xCostGod = true;
    }
    // Break-on-block: skip damage if enemy has block
    const skipDueToBlock = card.breakOnBlock && (state.enemy.block > 0);
    let hits = card.hits || 1;
    if (isXcost && card.xCostAll && card.damage) hits = Math.max(1, xSpent);
    let base = card.damage + (state.player.strength || 0);
    // v2.3 LEGENDARY: DOUBLE ARC — strength counted twice on damage.
    if (state.player._blessMods && state.player._blessMods.blessDoubleStr) {
      base += (state.player.strength || 0);
    }
    if (card.extraForBuffs) {
      const debuffs = (state.enemy.weak > 0 ? 1 : 0) + (state.enemy.vulnerable > 0 ? 1 : 0);
      base += debuffs * (2 + (card.extraForBuffsBonus || 0));
    }
    if (card.damagePerAttackPlayed) {
      base += card.damagePerAttackPlayed * (state.attacksThisTurn - 1);
    }
    if (card.scalePerAttackThisTurn) {
      base += card.scalePerAttackThisTurn * (state.attacksThisTurn - 1);
    }
    if (card.damageEqualsBlock) {
      base += state.player.block;
    }
    // v2.8 DIVINE: BRAINROT PRIME — +damage per gold (halve gold on play)
    if (card.damageFromGold && (state.player.gold || 0) > 0) {
      base += Math.floor((state.player.gold || 0) / card.damageFromGold);
    }
    // Combo bonus (if last played card matches type)
    if (card.comboBonus && card.comboBonus.applyTo !== 'block') {
      const lastType = state.lastPlayedType;
      if (lastType === card.comboBonus.afterType) base += card.comboBonus.bonus;
    }
    // consumeTag threshold bonus damage
    if (card.consumeTag && card.consumeTag.tag && typeof card.consumeTag.bonus === 'number') {
      const cnt = state.tagCountsCombat[card.consumeTag.tag] || 0;
      const thr = card.consumeTag.threshold || 3;
      if (cnt >= thr) base += card.consumeTag.bonus;
    }
    // Combo: damage per tag played
    if (card.dmgPerTagThisTurn) {
      for (const [t, per] of Object.entries(card.dmgPerTagThisTurn)) {
        const c = (state.tagCountsTurn[t] || 0) - (card.tags?.includes(t) ? 1 : 0);
        base += Math.max(0, c) * per;
      }
    }
    if (card.dmgPerTagInCombat) {
      for (const [t, per] of Object.entries(card.dmgPerTagInCombat)) {
        const c = (state.tagCountsCombat[t] || 0) - (card.tags?.includes(t) ? 1 : 0);
        base += Math.max(0, c) * per;
      }
    }
    if (card.dmgPerUniqueTagCombat) {
      const unique = state.uniqueTagsCombat;
      base += unique * card.dmgPerUniqueTagCombat;
    }
    if (card.bonusIfAttacksPlayed) {
      const already = (state.attacksThisTurn - 1);
      if (already >= card.bonusIfAttacksPlayed.count) base += card.bonusIfAttacksPlayed.bonus;
    }
    if (card.randomBonusMax) {
      const rng = makeRng(state.rngSeed + '-schizo-' + state.turn + '-' + state.attacksThisTurn);
      base += Math.floor(rng() * (card.randomBonusMax + 1));
    }
    // Scaling attacks: per-instance and global permanent scaling
    if (card.scalePerPlay) {
      const key = 'compound_' + cardId;
      const bonus = state.scaling?.[key] || 0;
      base += bonus;
      state.scaling = state.scaling || {};
      state.scaling[key] = bonus + card.scalePerPlay;
    }
    if (card.scaleGlobal) {
      state.globalScaling = state.globalScaling || {};
      const key = cardId;
      base += state.globalScaling[key] || 0;
      state.globalScaling[key] = (state.globalScaling[key] || 0) + card.scaleGlobal;
    }
    // v0.9.5 HP-conditional damage
    if (card.requireFullHp && state.player.hp === state.player.maxHp && card.damageIfFull) {
      base = card.damageIfFull + (state.player.strength || 0);
    }
    if (card.damageIfLowHp && state.player.hp / state.player.maxHp < 0.3) {
      base = card.damageIfLowHp + (state.player.strength || 0);
    }
    // Track lastPlayedId for DUPLICATE
    state.lastPlayedId = cardId;
    // Delusional: while low HP, +damage
    const lowHpMult = getLowHpMult(state);
    if (lowHpMult > 1) base = Math.floor(base * lowHpMult);
    // Retain damage bonus (cards held onto build up power)
    if (card.retainDmgBonus && state.retainDmgStack > 0) base += state.retainDmgStack;
    if (state.player.weak > 0) base = Math.floor(base * 0.75);
    // Curse: player damage multiplier
    if (state.playerDamageMult && state.playerDamageMult !== 1) base = Math.floor(base * state.playerDamageMult);
    // v0.9: permanent damage mult from PERMA-CURSE (carried on player across combats)
    const _permaMult = state.player._permaDamageMult || 1;
    if (_permaMult !== 1) base = Math.floor(base * _permaMult);
    // v3.5 PERK: GLASS CANNON — +25% attack damage
    if (state.player._perkDmgMult && state.player._perkDmgMult !== 1) {
      base = Math.floor(base * state.player._perkDmgMult);
    }
    const startHp = state.enemy.hp;
    if (!skipDueToBlock) {
      for (let i = 0; i < hits; i++) {
        let dmg = base;
        if (state.enemy.vulnerable > 0) dmg = Math.floor(dmg * 1.5);
        if (dmg > state.biggestSingleHit) state.biggestSingleHit = dmg;
        applyDamageToEnemy(state, dmg);
      }
      // v3.5 PERK: EXECUTIONER — instakill non-elite/non-boss enemies at <=N HP
      if (state.player._perkExecute && state.enemy.hp > 0 && state.enemy.hp <= state.player._perkExecute
          && !state.enemy.isElite && !state.enemy.isBoss) {
        const _preHp = state.enemy.hp;
        state.enemy.hp = 0;
        state.dmgDealtThisCombat = (state.dmgDealtThisCombat || 0) + _preHp;
        // trigger kill-heal too
        if (state.player._perkKillHeal) {
          state.player.hp = Math.min(state.player.maxHp, state.player.hp + state.player._perkKillHeal);
        }
        log(state, `EXECUTED ${state.enemy.name} at ${_preHp} HP!`);
      }
    }
    // v2.8 DIVINE: BRAINROT PRIME — halve gold after damage bonus was applied
    if (card.halveGoldOnPlay) {
      state.player.gold = Math.floor((state.player.gold || 0) / 2);
    }
    // v2.8 DIVINE: THE SINGULARITY — instakill non-boss enemies
    if (card.instakillNonBoss && !state.enemy.isBoss && state.enemy.hp > 0) {
      const _preHp = state.enemy.hp;
      state.enemy.block = 0;
      state.enemy.hp = 0;
      state.dmgDealtThisCombat = (state.dmgDealtThisCombat || 0) + _preHp;
      if (_preHp > state.biggestSingleHit) state.biggestSingleHit = _preHp;
      log(state, `THE SINGULARITY consumes ${state.enemy.name}!`);
    }
    // v2.8 DIVINE: RATIO'D — replay every attack in discard, then EXHAUST them
    if (card.replayDiscardAttacks) {
      const attackIds = state.discardPile.filter(id => CARDS[id]?.type === 'attack' && !CARDS[id]?.divine);
      let extraDmg = 0;
      for (const aid of attackIds) {
        const acard = CARDS[aid];
        let ad = (acard.damage || 0) + (state.player.strength || 0);
        const ahits = acard.hits || 1;
        for (let h = 0; h < ahits; h++) {
          let d = ad;
          if (state.enemy.vulnerable > 0) d = Math.floor(d * 1.5);
          applyDamageToEnemy(state, d);
          extraDmg += d;
        }
      }
      // Move all replayed attacks to exhaust
      state.discardPile = state.discardPile.filter(id => !(CARDS[id]?.type === 'attack' && !CARDS[id]?.divine));
      for (const aid of attackIds) state.exhaustPile.push(aid);
      if (extraDmg > state.biggestSingleHit) state.biggestSingleHit = extraDmg;
      if (attackIds.length > 0) log(state, `RATIO'D replayed ${attackIds.length} attacks for ${extraDmg} dmg.`);
    }
    if (card.applyVuln) state.enemy.vulnerable += card.applyVuln;
    if (card.applyWeak) {
      const amt = state.powerDoubleWeak ? card.applyWeak * 2 : card.applyWeak;
      state.enemy.weak += amt;
    }
    if (card.drawback) applyDamageToPlayer(state, card.drawback, true);
    if (card.selfHp) applyDamageToPlayer(state, card.selfHp, true);
    if (card.gainStrengthOnKill && startHp > 0 && state.enemy.hp === 0) {
      state.player.strength += card.gainStrengthOnKill;
      // v3.16 FIX: MOG kills grant "permanent" STR per card description. Track it in
      // _permaStrength so it survives combat-end propagation.
      state.player._permaStrength = (state.player._permaStrength || 0) + card.gainStrengthOnKill;
    }
    if (card.addToDiscardOnPlay) {
      state.discardPile.push(card.addToDiscardOnPlay);
    }
  }

  // v2.1 CARD AUDIT FIX: apply Weak/Vuln for ALL card types (skill cards like DISCOURSE/SCREAM-A
  // used to be silently ignored because these were nested inside the attack branch).
  if (card.type !== 'attack') {
    if (card.applyVuln) state.enemy.vulnerable += card.applyVuln;
    if (card.applyWeak) {
      const amt = state.powerDoubleWeak ? card.applyWeak * 2 : card.applyWeak;
      state.enemy.weak += amt;
    }
  }

  if (card.block) {
    let blk = card.block;
    if (card.blockPerCopium) {
      const c = (state.tagCountsCombat.copium || 0) - (card.tags?.includes('copium') ? 1 : 0);
      blk += Math.max(0, c) * card.blockPerCopium;
    }
    // Retain bonus block for BIDING TIME-like cards
    if (card.retainBlockBonus && state.retainBlockStack > 0) blk += state.retainBlockStack;
    // X-cost scaling block (COPIUM RESERVOIR)
    if (isXcost && card.xCostAll && !card.damage) blk *= Math.max(1, xSpent);
    // comboBonus applyTo block
    if (card.comboBonus && card.comboBonus.applyTo === 'block' && state.lastPlayedType === card.comboBonus.afterType) blk += card.comboBonus.bonus;
    state.player.block += blk;
  }
  if (card.heal) {
    state.player.hp = Math.min(state.player.maxHp, state.player.hp + card.heal);
  }
  // v2.8 DIVINE: TOUCH GRASS+ULTIMATE — full heal + +N permanent max HP
  if (card.fullHealAndMaxHp) {
    const bump = card.fullHealAndMaxHp;
    state.player.maxHp += bump;
    state.player._permaMaxHp = (state.player._permaMaxHp || 0) + bump;
    state.player._permaHpApplied = true;
    state.player.hp = state.player.maxHp; // full heal
  }
  // v2.8 DIVINE: oncePerRun tracker for divine_touchgrass
  if (card.oncePerRun && cardId === 'divine_touchgrass') {
    state.player._divineTouchGrassUsed = true;
  }
  // v2.8 DIVINE: SIGMA PROTOCOL — draw until full hand
  if (card.drawToFull) {
    const need = Math.max(0, MAX_HAND - state.hand.length);
    if (need > 0) drawCards(state, need);
  }
  // v2.8 DIVINE: SIGMA PROTOCOL — flag rest of turn to be free
  if (card.zeroCostThisTurn) {
    state._zeroCostThisTurn = true;
  }
  // v2.8 DIVINE: RIZZ CASCADE — every card played this turn triggers twice
  if (card.echoAllThisTurn) {
    state._echoAllTurn = true;
  }
  // Combo: heal if copium played this turn
  if (card.healIfCopiumThisTurn) {
    const c = (state.tagCountsTurn.copium || 0) - (card.tags?.includes('copium') ? 1 : 0);
    if (c > 0) {
      state.player.hp = Math.min(state.player.maxHp, state.player.hp + card.healIfCopiumThisTurn);
    }
  }
  // Doom loop power: applies weak per doom
  if (state.powerDoomStack && card.tags?.includes('doom')) {
    const amt = typeof state.powerDoomStack === 'number' ? state.powerDoomStack : 1;
    state.enemy.weak += amt;
  }
  // Draw variants
  if (card.fixedDraw) drawCards(state, card.fixedDraw);
  if (card.drawPerTag) {
    for (const [t, per] of Object.entries(card.drawPerTag)) {
      const c = (state.tagCountsCombat[t] || 0) - (card.tags?.includes(t) ? 1 : 0);
      const extra = card.drawPerTagBonus || 0;
      drawCards(state, Math.max(0, c) * per + extra);
    }
  }
  if (card.draw) {
    let n = card.draw;
    if (isXcost && card.xCostAll && !card.damage && !card.block) n = Math.max(1, xSpent);
    drawCards(state, n);
  }
  // v0.9: GREEDY GRAB draw per gold
  if (card.drawPerGold && state.player.gold) {
    const n = Math.min(3, Math.floor(state.player.gold / card.drawPerGold));
    if (n > 0) drawCards(state, n);
  }
  // v0.9: KEEP GOING scaling block
  if (card.keepGoingScaling) {
    state.keepGoingCount = (state.keepGoingCount || 0) + 1;
    state.player.block += (state.keepGoingCount - 1) * card.keepGoingScaling;
  }
  // v0.9: REINCARNATE transform random hand card
  if (card.transformRandom && state.hand.length > 0) {
    const rng = makeRng(state.rngSeed + '-tr-' + state.turn);
    const pool = [...CARD_POOL_COMMON, ...CARD_POOL_UNCOMMON, ...CARD_POOL_RARE];
    const targetIdx = Math.floor(rng() * state.hand.length);
    state.hand[targetIdx] = pool[Math.floor(rng() * pool.length)];
    log(state, `REINCARNATED a card.`);
  }
  // v0.9: ECHO CHAMBER — flag that next attack this turn plays twice
  if (card.duplicateNextAttack) state._echoNext = true;
  // v0.9: FREE TIER — reduce all hand cards costs
  if (card.reduceHandCosts) state._costReduction = (state._costReduction || 0) + card.reduceHandCosts;
  // v0.9: SPIRAL OF DOOM — exhaust hand
  if (card.exhaustHand) {
    while (state.hand.length > 0) {
      const cid = state.hand.pop();
      state.exhaustPile.push(cid);
    }
  }
  // v0.9.5 card-gen: add specific card to hand
  if (card.addToHand && state.hand.length < 10) {
    state.hand.push(card.addToHand);
  }
  // v0.9.5 duplicate previous card played
  if (card.duplicateHandCard && state.lastPlayedId && state.hand.length < 10) {
    state.hand.push(state.lastPlayedId);
  }
  // v0.9.5 recycler
  if (card.cycleDeck) {
    const n = state.hand.length;
    state.discardPile.push(...state.hand);
    state.hand = [];
    drawCards(state, n + 1);
  }
  // v0.9.5 on-kill gold
  if (card.onKillGold && state.enemy.hp === 0) {
    state.player.gold = (state.player.gold || 0) + card.onKillGold;
  }
  // v0.9.5 hopeful heal
  if (card.healOnSkill) {
    state.player.hp = Math.min(state.player.maxHp, state.player.hp + card.healOnSkill);
  }
  // v0.9.5 apply perma-max-hp immediately (HYPERBARIC)
  if (card.permaMaxHpApplyNow) {
    state.player.maxHp += (card.permaMaxHp || 0);
    state.player.hp += (card.permaMaxHp || 0);
    state.player._permaMaxHp = (state.player._permaMaxHp || 0) + (card.permaMaxHp || 0);
    state.player._permaHpApplied = true;
  }
  // v0.9.5 gain energy if enemy already debuffed (v2.1 uses pre-play snapshot)
  if (card.gainEnergyOnDebuffed && _wasDebuffedPrePlay) {
    state.player.energy += card.gainEnergyOnDebuffed;
  }
  // v0.9: permanent buffs for the rest of the run
  if (card.permaStrength) state.player._permaStrength = (state.player._permaStrength || 0) + card.permaStrength;
  if (card.permaMaxHp && !card.permaMaxHpApplyNow) state.player._permaMaxHp = (state.player._permaMaxHp || 0) + card.permaMaxHp;
  if (card.permaDamageMult) state.player._permaDamageMult = (state.player._permaDamageMult || 1) * card.permaDamageMult;
  // v0.8: tag-consumer STR gain (MOG SURGE)
  if (card.consumeTag && card.consumeTag.bonus === 'str') {
    const cnt = state.tagCountsCombat[card.consumeTag.tag] || 0;
    if (cnt >= (card.consumeTag.threshold || 3)) state.player.strength += (card.consumeTag.gain || 3);
  }
  // v0.8: skip enemy intent (TIME STOP)
  if (card.skipEnemyIntent) state._skipNextIntent = true;
  // Enemy STR debuff
  if (card.applyEnemyStrength) {
    state.enemy.strength = Math.max(0, (state.enemy.strength || 0) + card.applyEnemyStrength);
  }
  if (card.discardHand) {
    const remaining = state.hand.filter((_, i) => i !== handIdx);
    const rng = makeRng(state.rngSeed + '-dis-' + state.turn);
    const shuffled = shuffle(rng, remaining);
    state._pendingDiscards = shuffled.slice(0, card.discardHand);
  }
  if (card.energy) state.player.energy += card.energy;
  if (card.gainStrength) state.player.strength += card.gainStrength;
  if (card.reflect) {
    state.player.reflect = 1;
    state.player.reflectAmount = card.reflectAmount || 4;
  }
  if (card.dodgeNextAttack) state.player.dodgeNext = true;
  if (card.drainEnemyEnergy) state.intentDamageMod += card.drainEnemyEnergy;

  if (card.type === 'power') {
    state.powersPlayedThisCombat = (state.powersPlayedThisCombat || 0) + 1;
    if (card.powerDrawExtra) state.powerDrawExtra += card.powerDrawExtra;
    if (card.powerStrength) state.powerStrengthGain += card.powerStrength;
    if (card.powerBlockPerTurn) state.powerBlockPerTurn += card.powerBlockPerTurn;
    if (card.powerDoubleWeak) state.powerDoubleWeak = true;
    if (card.powerReduceIncoming) state.powerReduceIncoming += card.powerReduceIncoming;
    if (card.powerLowHpDamageBoost) { state._lowHpDmg = card.powerLowHpDamageBoost; state._lowHpThr = card.powerLowHpThreshold || 0.3; }
    if (card.powerDoomStack) state.powerDoomStack = card.powerDoomStack;
    // v1.5: Powers that add a card to hand each turn (persist on state as an array)
    if (card.powerAddToHandOnTurnStart) {
      state.powerAddToHandEachTurn = state.powerAddToHandEachTurn || [];
      state.powerAddToHandEachTurn.push(card.powerAddToHandOnTurnStart);
    }
    // Also permaStrength from powers applies as strength gain now for the run
    if (card.permaStrength) state.player.strength += card.permaStrength;
    log(state, `POWER: ${card.name}`);
  }

  // Parasocial-style bonus: gain energy if combo trigger met
  if (card.cardEnergyOnCombo && (state.tagCountsCombat.schizo || 0) >= 2) {
    state.player.energy += card.cardEnergyOnCombo;
  }

  // If this was a retain card being played, reset its stack (used it up)
  if (card.retainDmgBonus) state.retainDmgStack = 0;
  if (card.retainBlockBonus) state.retainBlockStack = 0;

  // v0.8: Evolution — track plays; when threshold hit, transform in place
  let evolvedInto = null;
  if (card.evolveAfter && card.evolvesTo) {
    state.evolveCounts = state.evolveCounts || {};
    state.evolveCounts[cardId] = (state.evolveCounts[cardId] || 0) + 1;
    if (state.evolveCounts[cardId] >= card.evolveAfter) {
      evolvedInto = card.evolvesTo;
      state.evolveCounts[cardId] = 0;
    }
  }

  // Track last-played type for combo cards on next play
  state.lastPlayedType = card.type;

  // remove card from hand
  const [removed] = state.hand.splice(handIdx, 1);
  const finalId = evolvedInto || removed;
  if (evolvedInto) log(state, `${card.name} EVOLVED → ${CARDS[evolvedInto]?.name || evolvedInto}!`);
  // v2.8 DIVINE: INFINITY LOOP — if hand was just this card, gain handEmpty bonus. Then return to hand.
  const _wasEmptyAfterRemove = state.hand.length === 0;
  if (card.handEmptyDraw && _wasEmptyAfterRemove) drawCards(state, card.handEmptyDraw);
  if (card.handEmptyEnergy && _wasEmptyAfterRemove) state.player.energy += card.handEmptyEnergy;
  if (card.returnToHand && state.hand.length < MAX_HAND) {
    state.hand.push(finalId);
  } else if (card.exhaust || card.type === 'power') {
    state.exhaustPile.push(finalId);
  } else {
    state.discardPile.push(finalId);
  }

  // Trigger onDiscard for other cards discarded via discardHand
  if (state._pendingDiscards) {
    for (const id of state._pendingDiscards) {
      const idx = state.hand.indexOf(id);
      if (idx >= 0) {
        state.hand.splice(idx, 1);
        state.discardPile.push(id);
        const dc = CARDS[id];
        if (dc?.onDiscard) {
          if (dc.onDiscard.block) state.player.block += dc.onDiscard.block;
          if (dc.onDiscard.draw) drawCards(state, dc.onDiscard.draw);
        }
      }
    }
    delete state._pendingDiscards;
  }

  log(state, `Played ${card.name}.`);
  checkEnd(state);

  // v0.9 ECHO CHAMBER: replay this attack if the echo flag is set.
  if (card.type === 'attack' && state._echoNext && !card._echoReplayed) {
    state._echoNext = false;
    // Push a synthetic copy of the card to the top of hand and immediately play it (free).
    // We simulate by placing the id back into hand, then playing (energy 0).
    const savedEnergy = state.player.energy;
    state.player.energy = savedEnergy + numericCost;
    state.hand.push(cardId);
    playCard(state, state.hand.length - 1);
    // no-op if the recursive call already handled it
  }
  // v2.8 DIVINE: RIZZ CASCADE — echo EVERY card played this turn (once). Skip if card already replayed to prevent infinite loop.
  if (state._echoAllTurn && !card.echoAllThisTurn && !state._echoAllReplaying && !card.returnToHand) {
    state._echoAllReplaying = true;
    const savedEnergy = state.player.energy;
    state.player.energy = savedEnergy + numericCost;
    state.hand.push(cardId);
    playCard(state, state.hand.length - 1);
    state._echoAllReplaying = false;
  }
  // v1.9 ECHO SHARD relic: first ATTACK card each combat plays twice
  if (card.type === 'attack' && !state._echoShardUsed && state.player.relics?.includes('echoshard') && !card._echoShardReplayed) {
    state._echoShardUsed = true;
    const savedEnergy = state.player.energy;
    state.player.energy = savedEnergy + numericCost;
    state.hand.push(cardId);
    // Mark the next replay so we don't loop
    const flaggedCard = { ...card, _echoShardReplayed: true };
    CARDS[`__echoshard_tmp_${cardId}`] = flaggedCard;
    // Just play again via normal path — the flag prevents further echo
    playCard(state, state.hand.length - 1);
    log(state, 'ECHO SHARD: attack replayed!');
  }
  return state;
}

function applyDamageToEnemy(state, dmg) {
  if (dmg <= 0) return;
  const _preHp = state.enemy.hp;
  state.dmgDealtThisCombat = (state.dmgDealtThisCombat || 0) + dmg;
  // v3.3 SHIELDED affix: absorb first N dmg per turn
  if (state.enemy.perTurnShield && !state.enemy._shieldConsumedThisTurn) {
    const absorbed = Math.min(state.enemy.perTurnShield, dmg);
    dmg -= absorbed;
    state.enemy._shieldConsumedThisTurn = absorbed;
    if (absorbed > 0) log(state, `SHIELD absorbs ${absorbed}`);
    if (dmg <= 0) return;
  }
  let remaining = dmg;
  if (state.enemy.block > 0) {
    const absorbed = Math.min(state.enemy.block, remaining);
    state.enemy.block -= absorbed;
    remaining -= absorbed;
  }
  if (remaining > 0) {
    state.enemy.hp = Math.max(0, state.enemy.hp - remaining);
    // v3.3 BARBED affix: reflect thorn damage to player
    if (state.enemy.barbedThorns && state.enemy.hp > 0) {
      applyDamageToPlayer(state, state.enemy.barbedThorns, true);
      log(state, `BARBED thorns → ${state.enemy.barbedThorns} back to you`);
    }
    // Vampiric affix
    if (state.enemy.vampiric && state.enemy.hp > 0) {
      const heal = Math.floor(remaining * state.enemy.vampiric);
      state.enemy.hp = Math.min(state.enemy.maxHp, state.enemy.hp + heal);
    }
    // Explosive death bomb
    if (state.enemy.hp === 0 && state.enemy.deathBomb && !state.enemy._exploded) {
      state.enemy._exploded = true;
      applyDamageToPlayer(state, state.enemy.deathBomb, true);
      log(state, `${state.enemy.name} EXPLODED for ${state.enemy.deathBomb}!`);
    }
    // v2.1 BOSS BLESSING: CAPITAL GAINS — +N gold on kill
    if (_preHp > 0 && state.enemy.hp === 0) {
      const g = state.player._blessMods?.blessGoldOnKill || 0;
      if (g > 0) {
        state.player.gold = (state.player.gold || 0) + g;
        log(state, `CAPITAL GAINS: +${g} GOLD`);
      }
      // v2.3 LEGENDARY: FEEDING FRENZY — draw a fresh hand on kill
      if (state.player._blessMods?.blessDrawOnKill) {
        drawCards(state, 5);
        log(state, `FEEDING FRENZY: fresh hand!`);
      }
      // v3.5 PERK: BLOOD PACT — +N HP per kill
      if (state.player._perkKillHeal) {
        state.player.hp = Math.min(state.player.maxHp, state.player.hp + state.player._perkKillHeal);
      }
    }
  }
}

function applyDamageToPlayer(state, dmg, ignoreBlock = false) {
  if (dmg <= 0) return;
  let remaining = dmg;
  // Chad-stance style flat damage reduction
  if (!ignoreBlock && state.powerReduceIncoming) {
    remaining = Math.max(0, remaining - state.powerReduceIncoming);
  }
  if (!ignoreBlock && state.player.block > 0) {
    const absorbed = Math.min(state.player.block, remaining);
    state.player.block -= absorbed;
    remaining -= absorbed;
  }
  if (remaining > 0) {
    // v3.5 PERK: IMMORTAL CORE — once per run, survive lethal at 1 HP
    if (state.player._perkImmortalAvail && state.player.hp - remaining <= 0 && state.player.hp > 0) {
      state.player.hp = 1;
      state.player._perkImmortalAvail = false;
      state.player._perkImmortalUsed = true;
      state.dmgTakenThisCombat = (state.dmgTakenThisCombat || 0) + remaining;
      log(state, `IMMORTAL CORE: survived lethal damage!`);
      return;
    }
    state.player.hp = Math.max(0, state.player.hp - remaining);
    state.dmgTakenThisCombat = (state.dmgTakenThisCombat || 0) + remaining;
    // v2.3 LEGENDARY: MIRROR AURA — reflect a % of damage taken back to attacker.
    const rp = state.player._blessMods?.blessReflectPct || 0;
    if (rp > 0 && state.enemy && state.enemy.hp > 0) {
      const reflected = Math.floor(remaining * rp);
      if (reflected > 0) {
        state.enemy.hp = Math.max(0, state.enemy.hp - reflected);
        log(state, `MIRROR AURA reflects ${reflected}!`);
      }
    }
  }
}

export function endTurn(state) {
  if (state.ended || state.turnOwner !== 'player') return state;
  state.turnOwner = 'enemy';
  // Handle retain / ethereal in hand
  const nextHand = [];
  for (const cid of state.hand) {
    if (isRetain(cid)) {
      nextHand.push(cid);
      const cdef = CARDS[cid] || {};
      if (cdef.retainDmgBonus) state.retainDmgStack = (state.retainDmgStack || 0) + cdef.retainDmgBonus;
      if (cdef.retainBlockBonus) state.retainBlockStack = (state.retainBlockStack || 0) + cdef.retainBlockBonus;
    } else if (isEthereal(cid)) {
      state.exhaustPile.push(cid);
    } else {
      state.discardPile.push(cid);
      // Fire onDiscard triggers at end-of-turn as well
      const dc = CARDS[cid] || {};
      if (dc.onDiscard) {
        if (dc.onDiscard.block) state.player.block += dc.onDiscard.block;
        if (dc.onDiscard.draw) state._pendingEndDraw = (state._pendingEndDraw || 0) + dc.onDiscard.draw;
      }
    }
  }
  state.hand = nextHand;
  const intent = state.enemy.intents[state.enemy.intentIdx];
  // v2.5 BOSS BUG FIX: reset enemy.block right BEFORE their action so the block they gain
  // during their turn PERSISTS through the player's next turn (StS-style behaviour).
  // Previously enemy.block was reset AFTER executeIntent → any block intent was immediately wiped.
  state.enemy.block = 0;
  if (state._skipNextIntent) {
    log(state, `${state.enemy.name}'s intent was SKIPPED!`);
    state._skipNextIntent = false;
  } else {
    executeIntent(state, intent);
    // v3.3 REGENERATING affix: enemy heals each of its own turns
    if (state.enemy.regenPerTurn && state.enemy.hp > 0) {
      const before = state.enemy.hp;
      state.enemy.hp = Math.min(state.enemy.maxHp, state.enemy.hp + state.enemy.regenPerTurn);
      if (state.enemy.hp > before) log(state, `REGEN: +${state.enemy.hp - before} HP`);
    }
    // v3.4 CHAINED affix: gain STR each turn (ramp up)
    if (state.enemy.strengthPerTurn) {
      state.enemy.strength = (state.enemy.strength || 0) + state.enemy.strengthPerTurn;
    }
    // Reset SHIELDED absorption for next turn
    state.enemy._shieldConsumedThisTurn = 0;
  }
  checkEnd(state);
  if (!state.ended) {
    state.turn += 1;
    // Cursed card HP drain each turn while card in deck/hand/discard/draw
    const allCards = [...state.hand, ...state.drawPile, ...state.discardPile];
    let drain = 0;
    for (const cid of allCards) {
      const c = CARDS[cid];
      if (c?.cursedDrain) drain += c.cursedDrain;
    }
    if (drain > 0) {
      state.player.hp = Math.max(1, state.player.hp - drain);
      log(state, `CURSED CARDS DRAIN ${drain} HP.`);
    }
    if (state.enemy.weak > 0) state.enemy.weak -= 1;
    if (state.enemy.vulnerable > 0) state.enemy.vulnerable -= 1;
    if (state.player.weak > 0) state.player.weak -= 1;
    if (state.player.vulnerable > 0) state.player.vulnerable -= 1;
    state.player.reflect = 0;
    state.player.reflectAmount = 0;
    state.player.block = 0;
    // v2.5 BOSS BUG FIX: DO NOT reset enemy.block here — it needs to persist through the
    // player's next turn so they must attack through it. Reset happens right before the
    // enemy's next intent (see top of endTurn).
    state.player.energy = state.player.maxEnergy + (state.player.relics?.includes('burnerphone') ? 1 : 0);
    // v1.9: AURA BANK — +1 energy every 3rd turn (turns 3, 6, 9, ...)
    if (state.player.relics?.includes('aurabank') && state.turn > 1 && (state.turn % 3 === 0)) {
      state.player.energy += 1;
      log(state, 'AURA BANK: +1 ENERGY');
    }
    if (state.powerStrengthGain > 0) state.player.strength += state.powerStrengthGain;
    if (state.powerBlockPerTurn > 0) state.player.block += state.powerBlockPerTurn;
    // v0.9: enemy blockPerTurn (from PLATED combat modifier)
    if (state.enemy.blockPerTurn) state.enemy.block += state.enemy.blockPerTurn;
    state.intentDamageMod = 0;
    state.attacksThisTurn = 0;
    state.lastTurnHadRatio = state.thisTurnHasRatio;
    state.thisTurnHasRatio = false;
    state.tagCountsTurn = {};
    state.synergiesFiredTurn = {};
    state._costReduction = 0;
    state._echoNext = false;
    state._echoAllTurn = false;         // v2.8 DIVINE
    state._zeroCostThisTurn = false;    // v2.8 DIVINE
    state._blessFirstFiredThisTurn = false;
    // Multi-phase boss transition (intents_by_phase array of arrays)
    if (state.enemy.phases && state.enemy.phases.length > 0) {
      const hpFrac = state.enemy.hp / state.enemy.maxHp;
      let newPhase = state.phase || 0;
      for (let i = state.enemy.phases.length - 1; i >= 0; i--) {
        if (hpFrac <= state.enemy.phases[i].threshold) { newPhase = i; break; }
      }
      if (newPhase !== state.phase) {
        state.phase = newPhase;
        state.enemy.intents = state.enemy.phases[newPhase].intents;
        state.enemy.intentIdx = 0;
        log(state, `${state.enemy.name}: PHASE ${newPhase + 1}!`);
      }
    }
    let drawN = 5 + (state.extraDraw || 0) + (state.powerDrawExtra || 0) + (state._pendingEndDraw || 0);
    state._pendingEndDraw = 0;
    // Retained cards count against draw
    drawN = Math.max(0, drawN - state.hand.length);
    drawCards(state, drawN);
    // v1.5: Powers that inject cards into hand each turn
    if (state.powerAddToHandEachTurn && state.powerAddToHandEachTurn.length) {
      for (const cid of state.powerAddToHandEachTurn) {
        if (state.hand.length < 10) state.hand.push(cid);
      }
    }
    const rng = makeRng(state.rngSeed + '-int-' + state.turn);
    let nextIdx;
    do {
      nextIdx = Math.floor(rng() * state.enemy.intents.length);
    } while (state.enemy.intents.length > 1 && nextIdx === state.enemy.intentIdx);
    state.enemy.intentIdx = nextIdx;
    state.turnOwner = 'player';
  }
  return state;
}

function executeIntent(state, intent) {
  if (!intent) return;
  if (intent.type === 'attack') {
    if (state.player.dodgeNext) {
      state.player.dodgeNext = false;
      log(state, `DODGED ${intent.label}!`);
      return;
    }
    const hits = intent.hits || 1;
    let base = intent.value + (state.enemy.strength || 0) - (state.intentDamageMod || 0);
    if (base < 1) base = 1;
    if (state.enemy.dmgMult && state.enemy.dmgMult !== 1) base = Math.floor(base * state.enemy.dmgMult);
    // v3.3 DOUBLED affix: +50% attack damage
    if (state.enemy.doubledIntent && state.enemy.doubledIntent !== 1) base = Math.floor(base * state.enemy.doubledIntent);
    // v3.4 POISONOUS affix: apply poison on hit
    if (state.enemy.applyPoisonOnHit) {
      state.player.poison = (state.player.poison || 0) + state.enemy.applyPoisonOnHit;
    }
    if (state.enemy.weak > 0) base = Math.floor(base * 0.75);
    for (let i = 0; i < hits; i++) {
      let dmg = base;
      if (state.player.vulnerable > 0) dmg = Math.floor(dmg * 1.5);
      applyDamageToPlayer(state, dmg);
      if (state.player.reflect > 0 && state.player.reflectAmount > 0 && state.enemy.hp > 0) {
        applyDamageToEnemy(state, state.player.reflectAmount);
      }
    }
    log(state, `${state.enemy.name}: ${intent.label}.`);
  } else if (intent.type === 'block') {
    state.enemy.block += intent.value;
    log(state, `${state.enemy.name}: ${intent.label}.`);
  } else if (intent.type === 'buff') {
    if (intent.buff === 'strength') state.enemy.strength += intent.value;
    log(state, `${state.enemy.name}: ${intent.label}.`);
  } else if (intent.type === 'debuff') {
    const extra = state.enemy.applyExtraWeak || 0;
    if (intent.debuff === 'weak') state.player.weak += intent.value + extra;
    if (intent.debuff === 'vulnerable') state.player.vulnerable += intent.value;
    log(state, `${state.enemy.name}: ${intent.label}.`);
  }
}

function checkEnd(state) {
  if (state.enemy.hp <= 0) {
    state.ended = true;
    state.victory = true;
    return;
  }
  if (state.player.hp <= 0) {
    state.ended = true;
    state.victory = false;
  }
}

// Compute the next N intents for an enemy based on the RNG seed (for boss telegraphs).
function computeIntentPreview(state, count = 2) {
  const previews = [];
  let idx = state.enemy.intentIdx;
  let turn = state.turn;
  for (let i = 0; i < count; i++) {
    const rng = makeRng(state.rngSeed + '-int-' + (turn + 1 + i));
    let next;
    do {
      next = Math.floor(rng() * state.enemy.intents.length);
    } while (state.enemy.intents.length > 1 && next === idx);
    previews.push(state.enemy.intents[next]);
    idx = next;
  }
  return previews;
}
export { computeIntentPreview };

function getLowHpMult(state) {
  if (state._lowHpDmg && state.player.hp / state.player.maxHp < state._lowHpThr) {
    return 1 + state._lowHpDmg;
  }
  return 1;
}

// ---- Reward generation ----
export function generateCardReward(rng, floor, count = 3, classId = null, opts = {}) {
  const rareBonus = floor >= 15 ? 0.06 : floor >= 10 ? 0.03 : 0;
  const rare = floor >= 5;
  const uncommon = floor >= 2;
  const out = [];
  const seen = new Set();
  let tries = 0;
  // v2.8 DIVINE: at most one divine per run, rolled ONCE for the whole reward.
  const divinePool = opts.divinePool || [];
  const divineAllowed = !!opts.divineAllowed && floor >= 5 && divinePool.length > 0;
  // v3.5 PERK: DIVINE INSIGHT doubles divine chance
  const divineChance = opts.divineBoost ? 0.08 : 0.04;
  if (divineAllowed && rng() < divineChance) {
    const pick = divinePool[Math.floor(rng() * divinePool.length)];
    out.push(pick);
    seen.add(pick);
  }
  while (out.length < count && tries < 40) {
    const roll = rng();
    let pool, pick;
    if (rare && roll < 0.12 + rareBonus) pool = CARD_POOL_RARE;
    else if (uncommon && roll < 0.5) pool = CARD_POOL_UNCOMMON;
    else pool = CARD_POOL_COMMON;
    pick = pool[Math.floor(rng() * pool.length)];
    tries++;
    // v1.9: no duplicates in the same 3-card reward
    if (seen.has(pick)) continue;
    seen.add(pick);
    out.push(pick);
  }
  // Backfill if we somehow couldn't fill (small pools)
  while (out.length < count) out.push(CARD_POOL_COMMON[Math.floor(rng() * CARD_POOL_COMMON.length)]);
  return out;
}
